'use client';

import { Box, LinearProgress, TableContainer, Typography, type SxProps } from '@mui/material';
import type { PagerData } from '@ocode/domain';
import { type Project, type ProjectBreedKey } from '@ocode/domain';
import { ApiRequestContext } from '@ocodelib/api-common';
import { flatSx, TopAbsolute } from '@ocodelib/mui';
import { useDebounceEffect, useLoadingVisible } from '@ocodelib/mui/hooks';
import { BbsPagination } from '@repo-foundation/components/BbsPagination';
import { BreedChips } from '@repo-ui/components/BreedChips';
import { NoDataView } from '@repo-ui/components/NoDataView';
import { ProjectListRow } from '@repo-ui/components/ProjectListRow';
import { useApi } from '@repo-ui/provider/useApi';
import { routerPushI18n } from '@repo-ui/router';
import { handleError } from '@repo-ui/util/handle-error';
import clsx from 'clsx';
import { useLocale } from 'next-intl';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

interface Props {
  sx?: SxProps;
  className?: string;
  breeds: ProjectBreedKey[];
}

type SearchParams = {
  rowsPerPage: number;
  pageNumber: number;
};

const DEFAULT_SEARCH_PARAMS = {
  pageNumber: 0,
  rowsPerPage: 30,
};

export function MyProjectList(props: Props) {
  const { sx, className, breeds } = props;
  const locale = useLocale();
  const api = useApi();
  const [searchParams, setSearchParams] = useState<SearchParams>(DEFAULT_SEARCH_PARAMS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadingVisible = useLoadingVisible(loading);
  const [pagerData, setPagerData] = useState<PagerData<Project>>();
  const [breed, setBreed] = useState<ProjectBreedKey>();

  // 프로젝트 목록 로드
  const doLoadProjectList = useCallback(
    async (ctx: ApiRequestContext, params: SearchParams, breed: ProjectBreedKey[]) => {
      setLoading(true);
      try {
        const { pagerData } = await api.project.list({
          ctx,
          ...params,
          breed,
        });
        if (ctx.canceled) return;
        setPagerData(pagerData);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // 프로젝트 목록 로드
  useDebounceEffect(
    () => {
      const ctx = ApiRequestContext.of();
      doLoadProjectList(ctx, searchParams, breed ? [breed] : breeds);
      return () => {
        ctx.cancel();
      };
    },
    [refreshToken, doLoadProjectList, searchParams, breed, breeds],
    { wait: 200 },
  );

  // 프로젝트 삭제
  const doDelete = useCallback(
    async (projectId: string): Promise<boolean> => {
      try {
        await api.project.delete({ projectId });
        return true;
      } catch (err) {
        handleError(err);
      }
      return false;
    },
    [api],
  );

  const handleClickRow = (project: Project) => () => {
    const { breed, projectId } = project;
    // 코딩 페이지로 이동, /en/js/project/xxxx
    routerPushI18n('/project', breed, projectId);
  };

  const handleClickDeleteBtn = (project: Project) => () => {
    if (!confirm(`[${project.projectName}] 삭제하시겠습니까?`)) return;
    doDelete(project.projectId).then((succ) => {
      if (succ) {
        toast.success('삭제되었습니다');
        setRefreshToken(Date.now());
      }
    });
  };

  // 페이지 번호 클릭
  const handleClickPageNumber = (pageNumber: number) => {
    setSearchParams((prev) => ({ ...prev, pageNumber }));
  };

  const {
    totalPages = 0,
    pageNumber = 0,
    elements = [],
    offset = 0,
    totalElements = 0,
  } = pagerData ?? {};
  const itemNumMax = totalElements - offset;

  return (
    <Box
      className={clsx('MyProjectList-root', className)}
      sx={flatSx(
        {
          position: 'relative',
          px: 2,
          pb: 5,
        },
        sx,
      )}
    >
      <BreedChips currentBreed={breed} breed={breeds} undefinedLabel="전체" onChange={setBreed} />

      <Box sx={{ mt: 3 }}>
        {totalElements > 0 && (
          <Typography variant="body1" sx={{ color: '#888', '& b': { color: '#444' } }}>
            전체 <b>{totalElements}</b>개
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '& .ProjectListRow-root': {
              borderBottom: '1px solid #ddd',
              minWidth: 630,
              '& + .ProjectListRow-root': {
                mt: 1,
              },
            },
          }}
        >
          <TableContainer>
            {elements.map((project, i) => (
              <ProjectListRow
                key={project.projectId}
                seq={itemNumMax - i}
                project={project}
                onClick={handleClickRow(project)}
                onClickDelete={handleClickDeleteBtn(project)}
              />
            ))}
          </TableContainer>
        </Box>

        <BbsPagination
          page={pageNumber}
          count={totalPages}
          disabled={loading}
          onChange={handleClickPageNumber}
        />
      </Box>

      <NoDataView visible={!loading && elements.length === 0} />

      {loadingVisible && (
        <TopAbsolute>
          <LinearProgress />
        </TopAbsolute>
      )}
    </Box>
  );
}
